import React from "react";
import { Document, Page, View, Text, Image as PdfImage, StyleSheet } from "@react-pdf/renderer";

// Fiche cuisine simplifiée — destinée au plan de travail du cuisinier.
// Aucun prix, aucun coût, aucune marge. Photo + nom + quantité massive.
// Lisible à 1 m de distance. 1 ou 2 produits par page selon nombre de lignes.

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const s = StyleSheet.create({
  page: { padding: 28, fontFamily: "Helvetica", color: "#0B0B0A", backgroundColor: "#FFFFFF" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderBottom: "2pt solid #0B0B0A",
    paddingBottom: 10,
    marginBottom: 18,
  },
  brand: { fontSize: 22, fontFamily: "Helvetica-Bold", letterSpacing: 0.5 },
  kicker: { fontSize: 9, color: "#575A57", letterSpacing: 1.4, textTransform: "uppercase", marginTop: 2 },
  refBlock: { alignItems: "flex-end" },
  ofRef: { fontSize: 14, fontFamily: "Helvetica-Bold" },
  ofDate: { fontSize: 10, color: "#575A57", marginTop: 2 },

  row: {
    flexDirection: "row",
    gap: 18,
    borderBottom: "1pt dashed #C8C8C0",
    paddingVertical: 16,
    alignItems: "center",
    minHeight: 230,
  },
  photoBox: {
    width: 220,
    height: 220,
    backgroundColor: "#F2F1ED",
    border: "1pt solid #DCDBD3",
    alignItems: "center",
    justifyContent: "center",
  },
  photoImg: { width: 220, height: 220, objectFit: "cover" },
  photoEmpty: { fontSize: 9, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 1 },

  detailsCol: { flex: 1, paddingLeft: 4 },
  productName: { fontSize: 26, fontFamily: "Helvetica-Bold", lineHeight: 1.15, marginBottom: 4 },
  productSku: { fontSize: 11, color: "#575A57", fontFamily: "Helvetica-Oblique", marginBottom: 14 },

  qtyBlock: { flexDirection: "row", alignItems: "baseline", gap: 10, marginBottom: 12 },
  qtyNumber: { fontSize: 68, fontFamily: "Helvetica-Bold", color: "#0B0B0A", lineHeight: 1 },
  qtyUnit: { fontSize: 18, color: "#575A57" },

  metaRow: { flexDirection: "row", gap: 22, marginTop: 8 },
  metaItem: { flexDirection: "column" },
  metaLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 },
  metaValue: { fontSize: 14, fontFamily: "Helvetica-Bold" },
  metaMono: { fontFamily: "Courier-Bold", fontSize: 14 },

  checkBoxes: { flexDirection: "row", gap: 14, marginTop: 14 },
  checkBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderTop: "0.5pt solid #DCDBD3",
    paddingTop: 6,
  },
  checkSquare: { width: 14, height: 14, border: "1.5pt solid #0B0B0A" },
  checkLabel: { fontSize: 9, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.8 },

  notes: {
    marginTop: 14,
    padding: 10,
    backgroundColor: "#FBF4E9",
    borderLeft: "3pt solid #8C6B2F",
  },
  notesLabel: { fontSize: 8, color: "#8C6B2F", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 },
  notesText: { fontSize: 11, color: "#5C4A1F" },

  footer: {
    position: "absolute",
    bottom: 18,
    left: 28,
    right: 28,
    fontSize: 8,
    color: "#A3A6A3",
    textAlign: "center",
    borderTop: "0.5pt solid #DCDBD3",
    paddingTop: 6,
  },
});

const photoUrlFor = (product, apiBase) => {
  if (!product) return null;
  if (product.photo_url) {
    // Forcer JPEG si l'URL pointe vers /photo (sinon react-pdf v4 ne lit pas WebP)
    if (product.photo_url.includes("/api/shop/products/") && product.photo_url.includes("/photo")) {
      return `${apiBase}/api/shop/products/${product.id}/photo.jpg`;
    }
    if (product.photo_url.startsWith("http")) return product.photo_url;
    return `${apiBase}${product.photo_url}`;
  }
  // fallback : essayer l'endpoint canonique
  return `${apiBase}/api/shop/products/${product.id}/photo.jpg`;
};

const OfKitchenLine = ({ line, product, apiBase }) => {
  const photoSrc = photoUrlFor(product, apiBase);
  return (
    <View style={s.row} wrap={false}>
      <View style={s.photoBox}>
        {photoSrc ? (
          <PdfImage src={photoSrc} style={s.photoImg} />
        ) : (
          <Text style={s.photoEmpty}>Pas de photo</Text>
        )}
      </View>
      <View style={s.detailsCol}>
        <Text style={s.productName}>{product?.name || line.product_id}</Text>
        {product?.sku ? <Text style={s.productSku}>{product.sku}</Text> : null}

        <View style={s.qtyBlock}>
          <Text style={s.qtyNumber}>{line.quantity_target}</Text>
          <Text style={s.qtyUnit}>{product?.unit ? `× ${product.unit}` : "barquettes"}</Text>
        </View>

        <View style={s.metaRow}>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>N° de lot à étiqueter</Text>
            <Text style={s.metaMono}>{line.lot_number || "—"}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>DLC à inscrire</Text>
            <Text style={s.metaValue}>{line.dlc ? fmtDate(line.dlc) : "—"}</Text>
          </View>
          {product?.net_weight ? (
            <View style={s.metaItem}>
              <Text style={s.metaLabel}>Poids net / barquette</Text>
              <Text style={s.metaValue}>{product.net_weight} g</Text>
            </View>
          ) : null}
        </View>

        <View style={s.checkBoxes}>
          <View style={s.checkBox}>
            <View style={s.checkSquare} />
            <Text style={s.checkLabel}>Préparé</Text>
          </View>
          <View style={s.checkBox}>
            <View style={s.checkSquare} />
            <Text style={s.checkLabel}>Mis en barquette</Text>
          </View>
          <View style={s.checkBox}>
            <View style={s.checkSquare} />
            <Text style={s.checkLabel}>Étiqueté</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export const OfKitchenDocument = ({ order, products, apiBase, companyName }) => {
  const lines = order?.lines || [];
  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.header} fixed>
          <View>
            <Text style={s.brand}>{companyName || "Maison Thaï"}</Text>
            <Text style={s.kicker}>Fiche cuisine · Production du jour</Text>
          </View>
          <View style={s.refBlock}>
            <Text style={s.ofRef}>OF · {order?.reference || "—"}</Text>
            <Text style={s.ofDate}>Date prévue : {fmtDate(order?.planned_date)}</Text>
          </View>
        </View>

        {lines.length === 0 ? (
          <Text style={{ fontSize: 12, color: "#575A57" }}>Aucune ligne sur cet ordre de fabrication.</Text>
        ) : (
          lines.map((ln, idx) => (
            <OfKitchenLine key={ln.id || idx} line={ln} product={productMap[ln.product_id]} apiBase={apiBase} />
          ))
        )}

        {order?.notes ? (
          <View style={s.notes}>
            <Text style={s.notesLabel}>Notes de l'OF</Text>
            <Text style={s.notesText}>{order.notes}</Text>
          </View>
        ) : null}

        <Text style={s.footer} fixed>
          Fiche cuisine · Lisible à 1 m · Ne contient ni prix ni marge · {companyName || "Maison Thaï"}
        </Text>
      </Page>
    </Document>
  );
};

export default OfKitchenDocument;
